import * as React from 'react'
import { IDriverSearchObject, IOperatorProviderObject } from '../../types'
import ImageLiveDrivers from '../ui/ImageLiveDrivers'
import { maskPhoneNumber } from '../../utils/mask'
import Toggle from '../ui/Toggle'
import PreloaderCircle from '../ui/PreloaderCircle'
import FloatingForm from '../ui/form/FloatingForm'
import FormLabel from '../ui/form/FormLabel'
import { Switch } from '@mobiscroll/react-latest'
import { driversService, IDriverAddress } from 'src/services/Drivers'

interface IOperatorPersonalInfoProps {
  driverInfo?: IDriverSearchObject
}

const OperatorViewPersonalInfo = (props: IOperatorPersonalInfoProps) => {
  const operator = props.driverInfo
  const [showReturn, setShowReturn] = React.useState(false)
  const [address, setAddress] = React.useState<IDriverAddress | null>(null)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowReturn(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  React.useEffect(() => {
    if (operator && operator.id) {
      fetchAddress(operator.id)
    }
  }, [operator])

  if (!operator) {
    return null
  }

  let phoneCountryCode = ''
  let phoneNumber = ''

  if (operator.countryCode) {
    phoneCountryCode = operator.countryCode.startsWith('+') ? operator.countryCode.substring(1) : operator.countryCode
  }

  if (!operator.phone || !operator.countryCode) {
    // If phone or countryCode is empty, set phoneNumber to empty
    phoneNumber = ''
  } else if (operator.phone.toString().startsWith(phoneCountryCode)) {
    phoneNumber = '+' + operator.phone
  } else {
    phoneNumber = '+' + phoneCountryCode + operator.phone
  }

  if (phoneNumber) {
    phoneNumber = maskPhoneNumber(phoneNumber)
  }

  const operatorVehicle = operator.vehicle
    ? `${operator.vehicle.make} ${operator.vehicle.model} ${operator.vehicle.licensePlate}`
    : 'Not checked in'

  const birthDateDisplay = operator.birthDate !== '' ? operator.birthDate.split('T')[0] : ''

  const fetchAddress = async (operatorId: string) => {
    const address = await driversService.getDriverAddress(operatorId)
    if (address.length > 0) {
      setAddress(address[0])
      return address
    } else {
      setAddress(null)
      return null
    }
  }

  if (showReturn) {
    return (
      <FloatingForm fullHeight={true}>
        <>
          <div className='form-modal-section main-section'>
            <div className='flex'>
              <FormLabel>Personal Information</FormLabel>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div style={{ height: '82px' }} className='flex flex-row'>
              <div className='flex flex-column justify-center'>
                <ImageLiveDrivers name={operator.name} entityId={operator.id} isRounded={true} />
              </div>
              <div className='ml2 flex flex-column justify-center'>
                <div className='flex flex-column mt1'>
                  <span className='RiderDetailsFirstName h5'>{operator.name}</span>{' '}
                  <span className='h5'>
                    Current Vehicle: <span className='font-blue bold h5'>{operatorVehicle}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className='flex'>
              <div className='col-6'>
                <div className='flex flex-row'>
                  <div style={{ flex: 1 }} className='mt1'>
                    <FormLabel>Phone Number</FormLabel>
                    <div className='mt1 h5'>{phoneNumber}</div>
                  </div>
                </div>

                <div className='flex flex-row'>
                  <div style={{ flex: 1 }} className='mt1'>
                    <FormLabel>Email</FormLabel>
                    <div className='mt1 h5'>{operator.email}</div>
                  </div>
                </div>

                <div className='flex flex-row'>
                  <div style={{ flex: 1 }} className='mt1'>
                    <FormLabel>Username</FormLabel>
                    <div className='mt1 h5'>{operator.username}</div>
                  </div>
                </div>

                <div className='flex flex-row'>
                  <div style={{ flex: 1 }} className='mt1'>
                    <FormLabel>Date of Birth</FormLabel>
                    <div className='mt1 h5'>{birthDateDisplay}</div>
                  </div>
                </div>

                <div className='flex flex-row'>
                  <div style={{ flex: 1 }} className='mt1'>
                    <FormLabel>Driver's License Number</FormLabel>
                    <div className='mt1 h5'>
                      {operator.driversLicense && operator.driversLicense !== '' ? operator.driversLicense : 'None'}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-6'>
                {address && address.street && (
                  <div className='flex flex-row'>
                    <div style={{ flex: 1 }} className='mt1'>
                      <FormLabel>Street Address</FormLabel>
                      <div className='mt1 h5'>{address ? address.street : ''}</div>
                    </div>
                  </div>
                )}

                {address && address.city && (
                  <div className='flex flex-row'>
                    <div style={{ flex: 1 }} className='mt1'>
                      <FormLabel>City</FormLabel>
                      <div className='mt1 h5'>{address ? address.city : ''}</div>
                    </div>
                  </div>
                )}

                {address && address.state && (
                  <div className='flex flex-row'>
                    <div style={{ flex: 1 }} className='mt1'>
                      <FormLabel>State</FormLabel>
                      <div className='mt1 h5'>{address ? address.state : ''}</div>
                    </div>
                  </div>
                )}

                {address && address.zip && (
                  <div className='flex flex-row'>
                    <div style={{ flex: 1 }} className='mt1'>
                      <FormLabel>Zip Code</FormLabel>
                      <div className='mt1 h5'>{address ? address.zip : ''}</div>
                    </div>
                  </div>
                )}

                {address && address.country && (
                  <div className='flex flex-row'>
                    <div style={{ flex: 1 }} className='mt1'>
                      <FormLabel>Country</FormLabel>
                      <div className='mt1 h5'>{address ? address.country : ''}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='form-modal-section sub-section'>
            <div className='flex'>
              <FormLabel>Additional Settings</FormLabel>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>Availability For Back-to-Back Trips</FormLabel>
                <Switch
                  checked={operator.isAvailableForBackToBack ? operator.isAvailableForBackToBack : false}
                  position='start'
                />
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>Dark Mode</FormLabel>
                <Switch
                  checked={operator.darkmode ? operator.darkmode : false}
                  onClick={() => void 0}
                  position='start'
                />
              </div>
            </div>
          </div>
        </>
      </FloatingForm>
    )
  }

  return (
    <div>
      <PreloaderCircle loading={true} />
    </div>
  )
}
export default OperatorViewPersonalInfo



// WEBPACK FOOTER //
// ./src/components/drivers/DriverViewPersonalInfo.tsx